import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import internals from '../internals/index.js';
import deepmerge from '../utils/deepmerge.js';
import { getContrastRatio, lighten, darken } from '../utils/colorManipulator.js';

var _excluded = ["primary", "error", "warning", "success", "common", "contrastThreshold", "gray", "mode", "overlay", "shadow", "tonalOffset", "brand", "navy", "red", "orange", "green"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var colors = internals.colors;
var light = {
  text: {
    title: colors.accent[900],
    primary: colors.accent[800],
    secondary: colors.accent[700],
    disabled: colors.accent[600]
  },
  border: colors.accent[500],
  divider: colors.accent[400],
  background: {
    "default": colors.accent[300],
    paper: colors.common.white,
    table: colors.accent[200]
  },
  action: {
    active: colors.primary[800],
    activeBackground: colors.primary[700],
    hover: colors.primary[800],
    hoverBorder: colors.primary[500],
    hoverBackground: colors.primary[400],
    selected: colors.primary[800],
    disabled: colors.accent[600],
    disabledBackground: colors.accent[300],
    focus: colors.primary[600]
  }
};
var dark = {
  text: {
    title: colors.accent[900],
    primary: colors.accent[800],
    secondary: colors.accent[700],
    disabled: colors.accent[600]
  },
  border: colors.accent[500],
  divider: colors.accent[400],
  background: {
    "default": colors.accent[300],
    paper: colors.common.white,
    table: colors.accent[200]
  },
  action: {
    active: colors.primary[800],
    activeBackground: colors.primary[700],
    hover: colors.primary[800],
    hoverBorder: colors.primary[500],
    hoverBackground: colors.primary[400],
    selected: colors.primary[800],
    disabled: colors.accent[600],
    disabledBackground: colors.accent[300],
    focus: colors.primary[600]
  }
};
function addLightOrDark(intent, direction, shade, tonalOffset) {
  var tonalOffsetLight = tonalOffset.light || tonalOffset;
  var tonalOffsetDark = tonalOffset.dark || tonalOffset * 1.5;
  if (!intent[direction]) {
    if (intent.hasOwnProperty(shade)) {
      intent[direction] = intent[shade];
    } else if (direction === 'light') {
      intent.light = lighten(intent.main, tonalOffsetLight);
    } else if (direction === 'dark') {
      intent.dark = darken(intent.main, tonalOffsetDark);
    }
  }
}
function createPalette(palette) {
  var _palette$primary = palette.primary,
      primary = _palette$primary === void 0 ? {
    light: colors.primary[700],
    main: colors.primary[800],
    dark: colors.primary[900]
  } : _palette$primary,
      _palette$error = palette.error,
      error = _palette$error === void 0 ? {
    light: colors.red[400],
    main: colors.red[500],
    dark: colors.red[600]
  } : _palette$error,
      _palette$warning = palette.warning,
      warning = _palette$warning === void 0 ? {
    light: colors.orange[300],
    main: colors.orange[500],
    dark: colors.orange[700]
  } : _palette$warning,
      _palette$success = palette.success,
      success = _palette$success === void 0 ? {
    light: colors.green[300],
    main: colors.green[500],
    dark: colors.green[700]
  } : _palette$success,
      _palette$common = palette.common,
      common = _palette$common === void 0 ? {
    black: colors.common.black,
    white: colors.common.white
  } : _palette$common,
      _palette$contrastThre = palette.contrastThreshold,
      contrastThreshold = _palette$contrastThre === void 0 ? 3 : _palette$contrastThre,
      _palette$gray = palette.gray,
      gray = _palette$gray === void 0 ? colors.gray : _palette$gray,
      _palette$mode = palette.mode,
      mode = _palette$mode === void 0 ? 'light' : _palette$mode,
      _palette$overlay = palette.overlay,
      overlay = _palette$overlay === void 0 ? colors.overlay : _palette$overlay,
      _palette$shadow = palette.shadow,
      shadow = _palette$shadow === void 0 ? colors.shadow : _palette$shadow,
      _palette$tonalOffset = palette.tonalOffset,
      tonalOffset = _palette$tonalOffset === void 0 ? 0.2 : _palette$tonalOffset,
      _palette$brand = palette.brand,
      brand = _palette$brand === void 0 ? colors.brand : _palette$brand,
      _palette$navy = palette.navy,
      navy = _palette$navy === void 0 ? colors.navy : _palette$navy,
      _palette$red = palette.red,
      red = _palette$red === void 0 ? colors.red : _palette$red,
      _palette$orange = palette.orange,
      orange = _palette$orange === void 0 ? colors.orange : _palette$orange,
      _palette$green = palette.green,
      green = _palette$green === void 0 ? colors.green : _palette$green,
      other = _objectWithoutProperties(palette, _excluded);
  function getContrastText(background) {
    var contrastText = getContrastRatio(background, dark.text.primary) >= contrastThreshold ? dark.text.primary : light.text.primary;
    return contrastText;
  }
  var augmentColor = function augmentColor(_ref) {
    var color = _ref.color,
        name = _ref.name,
        _ref$mainShade = _ref.mainShade,
        mainShade = _ref$mainShade === void 0 ? 500 : _ref$mainShade,
        _ref$lightShade = _ref.lightShade,
        lightShade = _ref$lightShade === void 0 ? 300 : _ref$lightShade,
        _ref$darkShade = _ref.darkShade,
        darkShade = _ref$darkShade === void 0 ? 700 : _ref$darkShade;
    var draftColor = _objectSpread({}, color);
    if (!color.main && color[mainShade]) {
      draftColor.main = color[mainShade];
    }
    if (!draftColor.hasOwnProperty('main')) {
      throw new Error(['Resi-UI: The color%s provided to augmentColor(color) is invalid.\n' + 'The color object needs to have a `main` property or a `%s` property.', name ? " (".concat(name, ")") : '', mainShade].join('\n'));
    }
    if (typeof draftColor.main !== 'string') {
      throw new Error(['Resi-UI: The color%s provided to augmentColor(color) is invalid.\n' + '`color.main` should be a string, but `%s` was provided instead.\n', name ? " (".concat(name, ")") : '', JSON.stringify(draftColor.main)].join('\n'));
    }
    addLightOrDark(draftColor, 'light', lightShade, tonalOffset);
    addLightOrDark(draftColor, 'dark', darkShade, tonalOffset);
    return draftColor;
  };
  var modes = {
    dark: dark,
    light: light
  };
  return deepmerge(_objectSpread({
    activeMode: mode,
    brand: brand,
    common: common,
    gray: gray,
    navy: navy,
    red: red,
    orange: orange,
    green: green,
    overlay: overlay,
    shadow: shadow,
    primary: augmentColor({
      color: primary,
      name: 'primary',
      mainShade: 800,
      lightShade: 700,
      darkShade: 900
    }),
    error: augmentColor({
      color: error,
      name: 'error'
    }),
    warning: augmentColor({
      color: warning,
      name: 'warning'
    }),
    success: augmentColor({
      color: success,
      name: 'success'
    }),
    contrastThreshold: contrastThreshold,
    getContrastText: getContrastText,
    augmentColor: augmentColor,
    tonalOffset: tonalOffset
  }, modes[mode]), other);
}

export default createPalette;
export { dark, light };
//# sourceMappingURL=createPalette.js.map
